import type { Concept } from '@yuan-math/curriculum';

/**
 * Grade 6 — the first middle-school slice.
 *
 * This is where arithmetic stops being the subject and starts being the tool.
 * Three things change at once, and the prerequisites below record which:
 *
 *   - number widens (negatives arrive, division by a fraction stops being
 *     nonsense, and a decimal point stops being a spectator),
 *   - comparison becomes multiplicative (ratio, rate, percent),
 *   - letters start standing for numbers in earnest — first for one unknown
 *     value, then for a whole range of them, then for two quantities at once,
 *     and
 *   - a data set stops being a list to read off and becomes something with a
 *     shape and a spread.
 *
 * Every concept here has a lesson and exercises. As always, `levels` says where
 * a concept is *typically* taught and gates nothing.
 */
export const MIDDLE_CONCEPTS: Concept[] = [
  /* -------------------------------------------------------------- number */
  {
    id: 'divide-fractions',
    title: 'Dividing Fractions',
    summary: 'Dividing by a fraction asks how many of it fit inside — which is why the answer can grow.',
    strand: 'fractions',
    levels: ['grade-6'],
    prerequisites: ['divide-unit-fractions', 'add-fractions'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'integers',
    title: 'Negative Numbers',
    summary: 'The number line does not stop at zero. Numbers below it behave by rules you can see.',
    strand: 'numbers',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['place-value', 'multi-digit-subtraction'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'exponents',
    title: 'Exponents',
    summary: 'Repeated multiplication, written compactly — and a new step in the order of operations.',
    strand: 'algebra',
    levels: ['grade-6', 'grade-8'],
    prerequisites: ['multiplication-facts', 'order-of-operations'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'prime-factorization',
    title: 'Prime Factorisation',
    summary: 'Every whole number is a product of primes, in exactly one way.',
    strand: 'number-theory',
    levels: ['grade-6'],
    prerequisites: ['prime-composite'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'decimal-operations',
    title: 'Multiplying and Dividing Decimals',
    summary: 'The digits are the ones you already know. The only new question is where the point goes.',
    strand: 'decimals',
    levels: ['grade-5', 'grade-6'],
    prerequisites: ['add-decimals', 'multiply-multi-digit'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'gcf-lcm',
    title: 'Greatest Common Factor and Lowest Common Multiple',
    summary: 'The largest number that divides both, and the smallest that both divide — two opposite questions with one method.',
    strand: 'number-theory',
    levels: ['grade-6'],
    prerequisites: ['prime-factorization', 'factors-multiples'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'absolute-value-opposites',
    title: 'Opposites and Absolute Value',
    summary: 'How far a number sits from zero, ignoring which side — which is why the answer is never negative.',
    strand: 'numbers',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['integers'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* --------------------------------------------------- ratio and percent */
  {
    id: 'ratios',
    title: 'Ratios',
    summary: 'Comparing two quantities by division rather than by difference.',
    strand: 'numbers',
    levels: ['grade-6'],
    prerequisites: ['multiply-fractions', 'simplify-fractions'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'unit-rates',
    title: 'Rates and Unit Rates',
    summary: 'A ratio between different units — and why "per one" makes any two of them comparable.',
    strand: 'numbers',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['ratios', 'division-meaning'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'percentages',
    title: 'Percentages',
    summary: 'A ratio out of one hundred, which is what makes unlike things comparable at a glance.',
    strand: 'numbers',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['ratios', 'add-decimals'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ------------------------------------------------------------- algebra */
  {
    id: 'algebraic-expressions',
    title: 'Algebraic Expressions',
    summary: 'Writing, reading and simplifying expressions — the grammar of algebra before its sentences.',
    strand: 'algebra',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['variables', 'exponents'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'one-variable-inequalities',
    title: 'Writing and Graphing Inequalities',
    summary: 'An equation names one number. An inequality names all the numbers that would do.',
    strand: 'algebra',
    levels: ['grade-6'],
    prerequisites: ['variables', 'integers'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'two-variable-relationships',
    title: 'Two Variables at Once',
    summary: 'When one quantity depends on another, a table, a rule and a graph are three views of the same thing.',
    strand: 'algebra',
    levels: ['grade-6'],
    prerequisites: ['algebraic-expressions', 'coordinate-plane'],
    status: 'available',
    estimatedMinutes: 30,
  },
  /* ------------------------------------------------------------ geometry */
  {
    id: 'coordinate-plane',
    title: 'The Coordinate Plane',
    summary: 'Two number lines crossed at zero, so any point on a plane has a name.',
    strand: 'geometry',
    levels: ['grade-6'],
    prerequisites: ['integers', 'coordinate-first-quadrant'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'area-triangles',
    title: 'Area of Triangles and Parallelograms',
    summary: 'Every straight-sided shape can be cut and rearranged into a rectangle you already know.',
    strand: 'geometry',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['area-rectangles', 'multiply-fractions'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'volume',
    title: 'Volume',
    summary: 'How much space a solid fills — and why that needs three measurements, not two.',
    strand: 'geometry',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['area-rectangles', 'volume-unit-cubes'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ---------------------------------------------------------- statistics */
  {
    id: 'data-displays',
    title: 'Data and Graphs',
    summary: 'Reading and building the pictures that summarise data — and noticing what they leave out.',
    strand: 'statistics',
    levels: ['grade-4', 'grade-6'],
    prerequisites: ['add-decimals'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'mean-median-mode',
    title: 'Averages',
    summary: 'Three ways to name a typical value, and why they disagree.',
    strand: 'statistics',
    levels: ['grade-6'],
    prerequisites: ['data-displays', 'division-meaning'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'histograms',
    title: 'Histograms and Statistical Questions',
    summary: 'Grouping measurements into intervals shows the shape of a data set that a list of numbers hides.',
    strand: 'statistics',
    levels: ['grade-6'],
    prerequisites: ['data-displays'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'data-variability',
    title: 'Spread and Variability',
    summary: 'Two data sets can share an average and share nothing else. Spread is the part the average hides.',
    strand: 'statistics',
    levels: ['grade-6', 'grade-7'],
    prerequisites: ['mean-median-mode'],
    status: 'available',
    estimatedMinutes: 35,
  },
];
